import _ from 'lodash';
import {
  create,
  edit,
  remove as removeTopoNode,
  updateDefaultControl,
  fetchAllTopoNodes
} from 'api/toponode';
import { REQUEST_SUCCESS, REQUEST_FAIL } from 'reducers/modal/actions';
import { getAllDevices } from 'reducers/device/actions';
import { LOCAL_RESOURCE_MAPPED_VALUES } from 'utils/appConstants';

const externallyManagedEdevs =
  window.env.REACT_APP_EXTERNALLY_MANAGED_EDEVS === 'true';
const maxPrimacyValue = parseInt(window.env.REACT_APP_PRIMACY_MAX_VALUE);

export const FETCH_INITIAL_TOPO_NODES_SUCCESS =
  'FETCH_INITIAL_TOPO_NODES_SUCCESS';
export const CREATE_TOPO_NODE_SUCCESS = 'CREATE_TOPO_NODE_SUCCESS';
export const EDIT_TOPO_NODE_SUCCESS = 'EDIT_TOPO_NODE_SUCCESS';
export const DELETE_TOPO_NODE_SUCCESS = 'DELETE_TOPO_NODE_SUCCESS';
export const UPDATE_DEFAULT_TOPONODE_CONTROL_SUCCESS =
  'UPDATE_DEFAULT_TOPONODE_CONTROL_SUCCESS';
export const SET_TOPONODE_DATA_SUCCESS = 'SET_TOPONODE_DATA_SUCCESS';
export const SET_ALL_TOPONODES_SUCCESS = 'SET_ALL_TOPONODES_SUCCESS';

export const fetchAllTopoNodeData = () => async (dispatch, getState) => {
  try {
    let topoNodes = await fetchAllTopoNodes();

    return dispatch({
      type: FETCH_INITIAL_TOPO_NODES_SUCCESS,
      data: topoNodes
    });
  } catch (err) {
    // Redundant catch-throw, but maintaining this until we update polling
    throw err;
  }
};

export const duplicateTopoNode = topoNodeId => async (dispatch, getState) => {
  try {
    dispatch({
      type: REQUEST_FAIL,
      message: `TopoNode ${topoNodeId} already exists`
    });
  } catch (err) {
    return dispatch({
      type: REQUEST_FAIL,
      err
    });
  }
};

export const createToponode = topoNode => async (dispatch, getState) => {
  try {
    await create(topoNode);
    const allTopoNodes = getState().topoNode.topoNodes;
    let newTopoNodes = allTopoNodes.slice();
    if (newTopoNodes.length) {
      const newTopoNode = { ...topoNode };
      newTopoNode.childTopoNodeIds = [];
      newTopoNode.primacy = maxPrimacyValue;
      if (externallyManagedEdevs) {
        newTopoNode.localNode = LOCAL_RESOURCE_MAPPED_VALUES.LOCAL;
      }
      if (newTopoNode.parentTopoNodeId) {
        const parentNode =
          newTopoNodes.find(
            topoNode => topoNode.topoNodeId === newTopoNode.parentTopoNodeId
          ).primacy || maxPrimacyValue;
        newTopoNode.primacy = parentNode - 1;
      }
      newTopoNodes.push(newTopoNode);
    } else {
      newTopoNodes = await fetchAllTopoNodes();
    }
    dispatch({
      type: REQUEST_SUCCESS,
      message: `Toponode ${topoNode.topoNodeId} create success`
    });
    dispatch({
      type: CREATE_TOPO_NODE_SUCCESS,
      data: newTopoNodes
    });
    return { topoNodeId: topoNode.topoNodeId };
  } catch (err) {
    dispatch({
      type: REQUEST_FAIL,
      message: `Toponode ${topoNode.topoNodeId} create failed`
    });
    throw err;
  }
};

export const deleteTopoNode = topoNodeId => async (dispatch, getState) => {
  try {
    await removeTopoNode(topoNodeId);
    // NOTE: Refetch toponodes and dispatch after delete, revisited during polling epic
    const updatedTopoNodes = await dispatch(fetchAllTopoNodeData());
    await dispatch(getAllDevices());
    dispatch({
      type: REQUEST_SUCCESS,
      message: `Toponode ${topoNodeId} delete success`
    });
    return updatedTopoNodes;
  } catch (err) {
    dispatch({
      type: REQUEST_FAIL,
      message: `Toponode ${topoNodeId} delete fail`
    });
    throw err;
  }
};

export const editTopoNode = topoNode => async (dispatch, getState) => {
  try {
    await edit(topoNode);
    // NOTE: Refetch toponodes and dispatch after edit, revisited during polling epic
    const updatedTopoNodes = await dispatch(fetchAllTopoNodeData());
    dispatch({
      type: REQUEST_SUCCESS,
      message: `Toponode ${topoNode.topoNodeId} edit success`
    });
    return updatedTopoNodes;
  } catch (err) {
    const dispatchArg = {
      type: REQUEST_FAIL
    };
    if (err.status >= 500) {
      dispatchArg.message = `Toponode ${
        topoNode.topoNodeId || topoNode.parentTopoNodeId
      } edit failed`;
    } else {
      dispatchArg.message = err.message;
    }
    dispatch(dispatchArg);
    throw err;
  }
};

/**
 * Updates a specific toponode in the redux store.
 * @param {object} updatedTopoNode - a toponode object
 */
export const setTopoNodeData =
  updatedTopoNode => async (dispatch, getState) => {
    try {
      const currentTopoNodes = getState().topoNode.topoNodes;
      const updatedTopoNodes = currentTopoNodes.map(topoNode =>
        topoNode.topoNodeId === updatedTopoNode.topoNodeId
          ? updatedTopoNode
          : topoNode
      );
      return dispatch({
        type: SET_TOPONODE_DATA_SUCCESS,
        data: updatedTopoNodes
      });
    } catch (err) {
      // Redundant catch-throw, but maintaining this until we update polling
      throw err;
    }
  };

export const updateToponodeDefaultControl =
  (nodeId, control) => async (dispatch, getState) => {
    try {
      const allToponodes = getState().topoNode.topoNodes;

      const filteredPayload = _.omitBy(
        control,
        value => value === null || value === '' || _.isNaN(value)
      );

      const updatedTopoNodes = allToponodes.map(topoNode => {
        if (topoNode.topoNodeId === nodeId) {
          const newTopoNode = { ...topoNode };
          newTopoNode.defaultControl = control;
          return newTopoNode;
        }
        return topoNode;
      });

      await updateDefaultControl(nodeId, filteredPayload);
      dispatch({
        type: REQUEST_SUCCESS,
        message: `Toponode ${nodeId} edit default control success`
      });
      return dispatch({
        type: UPDATE_DEFAULT_TOPONODE_CONTROL_SUCCESS,
        data: updatedTopoNodes
      });
    } catch (err) {
      dispatch({
        type: REQUEST_FAIL,
        message: `Toponode ${nodeId} edit default control fail`
      });
      throw err;
    }
  };

export const setTopoNodes = topoNodes => async dispatch => {
  try {
    return dispatch({
      type: SET_ALL_TOPONODES_SUCCESS,
      data: topoNodes
    });
  } catch (err) {
    // Redundant catch-throw, but maintaining this until we update polling
    throw err;
  }
};

export const setTopoNodeDefaultControl =
  (topoNodeId, defaultControl) => async (dispatch, getState) => {
    try {
      const topoNodes = getState().topoNode.topoNodes;
      const updatedTopoNodes = topoNodes.map(topoNode => {
        if (topoNode.topoNodeId === topoNodeId) {
          const newTopoNode = { ...topoNode };
          newTopoNode.defaultControl = defaultControl;
          return newTopoNode;
        }
        return topoNode;
      });
      return dispatch({
        type: UPDATE_DEFAULT_TOPONODE_CONTROL_SUCCESS,
        data: updatedTopoNodes
      });
    } catch (err) {
      // Redundant catch-throw, but maintaining this until we update polling
      throw err;
    }
  };
